import { ApplicationDependencyService, ApplicationGroupingService, DatabaseService, ServerService } from '@amzn/awscat-portfolio-assessment-service-client';
import { ApplicationGroup, ApplicationToServer, DatabaseApplication } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, ButtonDropdown, ButtonDropdownProps, NonCancelableCustomEvent, Select, SelectProps, SpaceBetween } from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router-dom';

import { constants } from '../../../common/Localization';
import AWSCATTable from '../../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../../common/table/types';
import { MatchParam } from '../../../common/types';
import ConfirmationModal from '../../common/ConfirmationModal';
import { EnhancedApplication, STATUS_IN_GROUPING } from './constants';

type Props = FlashContextInterface & {
    onConfirm: () => void;
    onDismiss: () => void;
    groups: ApplicationGroup[];
    applicationMap: Map<string, EnhancedApplication>;
    appServerMappings: ApplicationToServer[];
    appDatabaseMappings: DatabaseApplication[];
};

type InfrastructureResource = {
    resourceId: string;
    resourceType: 'Server' | 'Database';
    applicationCount: number;
    isInfrastructure: boolean;
    overrideValue: boolean;
    groupName: string;
};

const AllApplicationsGroup = 'ALL_APPS';

const serverService = new ServerService();
const databaseService = new DatabaseService();
const applicationGroupingService = new ApplicationGroupingService();
const applicationDependencyService = new ApplicationDependencyService();

function SharedInfrastructureModal( { onConfirm, onDismiss, groups, flash, applicationMap, appServerMappings, appDatabaseMappings }: Props): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId || '';

    const [ loading, setLoading ] = useState(false);
    const [ selectedGroup, setSelectedGroup ] = useState<SelectProps.Option | null>(null);
    const [ portfolioResources, setPortfolioResources ] = useState<InfrastructureResource[]>([]);
    const [ selectedResources, setSelectedResources ] = useState<InfrastructureResource[]>([]);
    const [ dirtyCount, setDirtyCount ] = useState(0);

    const tableColumns: ColumnDefinition<InfrastructureResource>[] = [
        {
            header: constants.RESOURCE_TYPE,
            type: ColumnTypes.TEXT,
            id: 'resourceType',
            cell: item => item.resourceType,
            sortingField: 'resourceType',
        },
        {
            header: constants.RESOURCE_ID,
            type: ColumnTypes.TEXT,
            id: 'resourceId',
            cell: item => item.resourceId,
            sortingField: 'resourceId',
        },
        {
            header: constants.APPLICATION_COUNT,
            type: ColumnTypes.TEXT,
            id: 'applicationCount',
            cell: item => item.applicationCount,
            sortingField: 'applicationCount',
        },
        {
            header: constants.IS_SHARED_INFRASTRUCTURE,
            type: ColumnTypes.TEXT,
            id: 'isInfrastructure',
            data: item => item.overrideValue ? constants.TRUE : constants.FALSE,
            cell: item => item.overrideValue ? constants.TRUE : constants.FALSE,
            sortingField: 'isInfrastructure',
        },
    ];

    const loadData = async () => {
        setLoading(true);
        try {
            const servers = await serverService.list(portfolioId, true);
            const databases = await databaseService.list(portfolioId, true);

            const serverToIsInfrastructureMap: {[key: string]: boolean} = {};
            const serverToAppIdsMap: {[key: string]: string[]} = {};
            const serverToGroupNameMap: {[key: string]: string} = {};

            const databaseToIsInfrastructureMap: {[key: string]: boolean} = {};
            const databaseToAppIdsMap: {[key: string]: string[]} = {};
            const databaseToGroupNameMap: {[key: string]: string} = {};

            servers.forEach(server => serverToIsInfrastructureMap[server.serverId] = server.isSharedInfrastructure);
            databases.forEach(database => databaseToIsInfrastructureMap[database.databaseId] = database.isSharedInfrastructure ?? false);

            // extract a list of servers that are parts of groups and store corresponding information such as appIds and groupName using mappings
            appServerMappings.forEach(mapping => {
                const { serverId, applicationId, statusInGrouping, inScope } = mapping;
                const application = applicationMap.get(applicationId);
                if (mapping && serverId && applicationId && application && statusInGrouping === STATUS_IN_GROUPING.ACTIVE && inScope) {
                    if (!serverToAppIdsMap[serverId]) {
                        serverToAppIdsMap[serverId] = [];
                    }
                    serverToAppIdsMap[serverId].push(applicationId);
                    if(!serverToGroupNameMap[serverId] && !serverToIsInfrastructureMap[serverId]) {
                        serverToGroupNameMap[serverId] = application.groupName;
                    }
                }
            });

            // extract a list of databases that are parts of groups and store corresponding information such as appIds and groupName using mappings
            appDatabaseMappings.forEach(mapping => {
                const { databaseId, applicationId, statusInGrouping, inScope } = mapping;
                const application = applicationMap.get(applicationId);
                if (mapping && databaseId && applicationId && application && statusInGrouping === STATUS_IN_GROUPING.ACTIVE && inScope) {
                    if (!databaseToAppIdsMap[databaseId]) {
                        databaseToAppIdsMap[databaseId] = [];
                    }
                    databaseToAppIdsMap[databaseId].push(applicationId);
                    if(!databaseToGroupNameMap[databaseId] && !databaseToIsInfrastructureMap[databaseId]) {
                        databaseToGroupNameMap[databaseId] = application.groupName;
                    }
                }
            });

            // aggreagate the information extracted into a InfrastructureResource Type so the table can display it
            const serverTableItems: InfrastructureResource[] = Object.keys(serverToAppIdsMap).filter(serverId =>
                serverToIsInfrastructureMap.hasOwnProperty(serverId)).map(serverId => ({
                resourceId: serverId,
                resourceType: 'Server',
                applicationCount: serverToAppIdsMap[serverId].length,
                isInfrastructure: !!serverToIsInfrastructureMap[serverId],
                overrideValue: serverToIsInfrastructureMap[serverId],
                groupName: serverToGroupNameMap[serverId],
            })
            );

            // aggreagate the information extracted into a InfrastructureResource Type so the table can display it
            const databaseTableItems: InfrastructureResource[] = Object.keys(databaseToAppIdsMap).filter(databaseId =>
                databaseToIsInfrastructureMap.hasOwnProperty(databaseId)).map(databaseId => ({
                resourceId: databaseId,
                resourceType: 'Database',
                applicationCount: databaseToAppIdsMap[databaseId].length,
                isInfrastructure: !!databaseToIsInfrastructureMap[databaseId],
                overrideValue: databaseToIsInfrastructureMap[databaseId],
                groupName: databaseToGroupNameMap[databaseId],
            })
            );

            setPortfolioResources(([] as InfrastructureResource[]).concat(serverTableItems, databaseTableItems));

        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_REMOVING_APPLICATION);
        } finally {
            setLoading(false);
        }
    };

    const getTableItems = (groupName?: string): InfrastructureResource[] => sortResources(portfolioResources.filter(resource =>
        groupName ? groupName === constants.ALL_APPLICATIONS || (resource.groupName === groupName && !resource.overrideValue) :
            selectedGroup?.label === constants.ALL_APPLICATIONS || (resource.groupName === selectedGroup?.label && !resource.overrideValue)
    ));

    const sortResources = (resources: InfrastructureResource[]): InfrastructureResource[] =>
        resources.sort((a,b) => b.applicationCount - a.applicationCount);

    const countDirtyResources = (resources: InfrastructureResource[]): number => {
        return resources.reduce((count, resource) => count + (resource.overrideValue !== resource.isInfrastructure ? 1 : 0), 0);
    };

    useEffect(() => {
        loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onConfirmClick = async () => {
        setLoading(true);
        try {
            await applicationGroupingService.createGroups(portfolioId);
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_CREATING_GROUPS);
        } finally {
            onConfirm();
            setLoading(false);
        }
    };

    const selectGroupOptions = (): SelectProps.Options | undefined => {
        if (groups.length) {
            const groupOptions = [ { value: AllApplicationsGroup, label: constants.ALL_APPLICATIONS } ];
            groups.forEach(group => {
                if (group.applicationGroupId !== 'shared-infrastructure') {
                    groupOptions.push({
                        value: group.applicationGroupId,
                        label: group.name,
                    });
                }
            });
            return groupOptions;
        }
        return undefined;
    };

    const onGroupChange = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>): void => {
        const groupToSelect = event.detail.selectedOption;
        const newDirtyCount = countDirtyResources(getTableItems(groupToSelect.label));
        setSelectedGroup(groupToSelect);
        setDirtyCount(newDirtyCount);
    };

    const onTableSelectionChange = (items: readonly InfrastructureResource[] | undefined): void => {
        setSelectedResources(items ? [...items] : []);
    };

    const onStatusChangeClick = async ({ detail }: NonCancelableCustomEvent<ButtonDropdownProps.ItemClickDetails>): Promise<void> => {
        const updatedValue = detail.id === 'true';
        const serversToUpdate: string[] = [];
        const databasesToUpdate: string[] = [];
        selectedResources.forEach(item => {
            const { resourceType, overrideValue, resourceId } = item;
            if (resourceType === 'Server' && overrideValue !== updatedValue) {
                serversToUpdate.push(resourceId);
            } else if (resourceType === 'Database' && overrideValue !== updatedValue) {
                databasesToUpdate.push(resourceId);
            }
            const itemToUpdate = portfolioResources.find(resource => resource.resourceId === resourceId && resource.resourceType === resourceType);
            if (itemToUpdate) {
                itemToUpdate.overrideValue = updatedValue;
            }
        });
        setSelectedResources([]);
        setDirtyCount(countDirtyResources(getTableItems()));
        setPortfolioResources([...portfolioResources]);
        if (serversToUpdate.length || databasesToUpdate.length) {
            setLoading(true);
            try {
                await applicationDependencyService.updateSharedInfrastructure({
                    serverIds: serversToUpdate,
                    databaseIds: databasesToUpdate,
                    isSharedInfrastructure: updatedValue ? '1' : '0',
                    portfolioId,
                });
            } catch (err) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_UPDATING_SHARED_INFRA);
            } finally {
                setLoading(false);
            }
        }
    };

    const headerActions = (
        <ButtonDropdown
            items={[
                {
                    id: 'true',
                    text: constants.SHARED_INFRASTRUCTURE,
                },
                {
                    id: 'false',
                    text: constants.REGULAR_RESOURCE,
                }
            ]}
            onItemClick={onStatusChangeClick}
            disabled={!selectedResources.length}
        >
            {constants.UPDATE_BUTTON_TEXT}
        </ButtonDropdown>
    );

    return (
        <ConfirmationModal
            visible
            header={constants.MARK_AS_SHARED_INFRASTRUCTURE}
            confirmButton={constants.RECREATE_GROUPS_BUTTON_TEXT}
            onConfirm={onConfirmClick}
            confirmLoading={loading}
            onDismiss={onDismiss}
            size='max'
        >
            <Box className='awscatui-min-height-20rem'>
                <SpaceBetween size='l'>
                    <Select options={selectGroupOptions()} selectedOption={selectedGroup} onChange={onGroupChange} />
                    {selectedGroup ? (
                        <Box>
                            <AWSCATTable
                                resourceName={constants.RESOURCES}
                                items={getTableItems()}
                                columnDefinitions={tableColumns}
                                headerActions={headerActions}
                                resizeable
                                selectionType='multi'
                                onSelectionChange={onTableSelectionChange}
                                enableAutoSorting
                                autoGenerateVisibleContent
                                useDefaultDownload
                            />
                            <Box float='right'>
                                <p>
                                    {`${dirtyCount} ${dirtyCount === 1 ?
                                        constants.RESOURCE_LOWERCASE :
                                        constants.RESOURCES_LOWERCASE} ${constants.MODIFIED}.`}
                                </p>
                            </Box>
                        </Box>
                    )
                        : null}
                </SpaceBetween>
            </Box>
        </ConfirmationModal>
    );
}

export default withFlashContext(SharedInfrastructureModal);
